import { AxiosError } from "axios";
import InputField from "components/fields/InputField";
import { useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { authAPI } from "services/auth";
import { getAccessToken } from "utils/auth";
import { FormSchema } from "./form";
import { useAppStore } from "store/appStore";
import { Admin } from "types/auth";

export default function SignIn() {
  const [step, setStep] = useState<"login" | "secondStep">("login");
  const navigate = useNavigate();
  const { setAdmin } = useAppStore();

  useLayoutEffect(() => {
    try {
      getAccessToken();
      navigate("/");
    } catch (error) {
      console.warn(error);
    }
  }, []);

  const form = useForm<FormSchema>({
    defaultValues: {
      email: "",
      password: "",
      code: "",
      keepMeLoggedIn: false,
    },
  });

  const qrCodeUrl = form.watch("qrCodeUrl");

  const handleSubmit = async (data: FormSchema) => {
    try {
      if (step === "login") {
        const res = await authAPI.auth(data.email, data.password);
        if (res.authInfo) {
          setAdmin(res.authInfo.admin);
          navigate("/");
        } else if (res.twoFactorRequired) {
          setStep("secondStep");
        }
      }

      if (step === "secondStep") {
        const res = await authAPI.authWith2FA(
          data.email,
          data.password,
          data.code
        );

        res?.admin && setAdmin(res.admin);
        navigate("/");
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        form.setError("root", {
          type: "manual",
          message: error?.response?.data?.message || error.message,
        });
      } else if (error instanceof Error) {
        form.setError("root", {
          type: "manual",
          message: error.message,
        });
      }
      console.warn(error);
    }
  };

  return (
    <div className="mx-auto mb-16 mt-16 flex h-full w-full grow items-center justify-center px-2 md:px-0 lg:mb-10 lg:items-center">
      {/* Sign in section */}
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="mx-auto mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:max-w-[420px] lg:pl-0"
      >
        <h4 className="mb-2.5 text-center text-4xl font-bold text-navy-700 dark:text-white">
          Sign In
        </h4>
        {step === "login" && (
          <>
            <p className="mb-9 ml-1 text-center text-base text-gray-600">
              Enter your email and password to sign in!
            </p>
            {/* Email */}
            <InputField
              variant="auth"
              extra="mb-3"
              label="Email*"
              placeholder="mail@simmmple.com"
              id="email"
              type="email"
              required
              {...form.register("email", { required: true })}
            />
            {form.formState.errors.email && (
              <p className="text-sm text-red-500 dark:text-red-400">
                {form.formState.errors.email.message}
              </p>
            )}
            <InputField
              variant="auth"
              extra="mb-3"
              label="Password*"
              placeholder="password"
              id="password"
              type="password"
              required
              {...form.register("password", {
                required: step === "login",
              })}
            />
            {form.formState.errors.password && (
              <p className="text-sm text-red-500 dark:text-red-400">
                {form.formState.errors.password.message}
              </p>
            )}
          </>
        )}
        {step === "secondStep" && (
          <>
            <p className="mb-1 ml-1 text-center text-base text-gray-600">
              Enter code to complete two-factor authentication
            </p>

            <div className="flex flex-col items-center justify-center">
              <p className="text-sm">Use Google Authenticator</p>
            </div>

            <InputField
              variant="auth"
              extra="mb-3"
              label="One time code*"
              placeholder="_ _ _ _ _ "
              id="secondStepCode"
              type="text"
              required
              {...form.register("code", { required: true })}
            />
          </>
        )}
        {form.formState.errors.root && (
          <p className="text-sm text-red-500 dark:text-red-400">
            {form.formState.errors.root.message}
          </p>
        )}
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          {step === "login" && "Sign In"}
          {step === "secondStep" && "Confirm"}
        </button>
      </form>
    </div>
  );
}
